// TikTok OAuth callback landing page.
// Users arrive here after TikTok redirects them back through
// /api/brand-os/publish/oauth/tiktok/callback. One creator account per
// token, so this is a one-row confirmation picker.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function humanizeError(code) {
  const m = {
    missing_code_or_state: "The redirect from TikTok was missing the code or state parameter.",
    state_expired: "The connect flow timed out before it finished. Please start again.",
    state_malformed: "Something altered the OAuth state. Please start the connect flow again.",
    state_invalid_signature: "The OAuth state failed its signature check. Please start the connect flow again.",
    state_channel_mismatch: "That OAuth state wasn't issued for TikTok.",
    code_exchange_failed: "TikTok refused the authorization code. Please try the connect flow again.",
    userinfo_failed: "TikTok accepted the token but refused to describe the account.",
    tiktok_unreachable: "We couldn't reach TikTok. Try again in a minute.",
  };
  return m[code] || code;
}

export default function TikTokOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const errorFromTikTok = params.get("error");
  // When the backend bounces back with a user/info failure, these two
  // query params carry TikTok's own `error.code` / `error.message`
  // verbatim so the operator can see the real cause (e.g. a
  // `scope_not_authorized` from asking for a field the current scopes
  // don't allow).
  const tiktokCode = params.get("tiktok_code") || "";
  const tiktokMessage = params.get("tiktok_message") || "";

  const [session, setSession] = useState(null);
  const [pickedId, setPickedId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [submitState, setSubmitState] = useState({ submitting: false, error: null });

  const bounceBack = useCallback((flash, err) => {
    const search = new URLSearchParams();
    if (flash) search.set("oauth_result", encodeURIComponent(flash));
    if (err) search.set("oauth_error", encodeURIComponent(err));
    navigate({
      pathname: "/brand-os/admin/channel-connections",
      search: `?${search.toString()}`,
    }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (errorFromTikTok) {
      setLoadState({ loading: false, error: humanizeError(errorFromTikTok) });
      return;
    }
    if (!sessionId) {
      setLoadState({ loading: false, error: "Missing session id in URL." });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/publish/oauth/tiktok/session/${sessionId}`);
        setSession(r.data);
        const accounts = r.data.pages || [];
        if (accounts.length >= 1) {
          setPickedId(String(accounts[0].id));
          setDisplayName(accounts[0].title ? `TikTok · ${accounts[0].title}` : "");
        }
        setLoadState({ loading: false, error: null });
      } catch (e) {
        setLoadState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [sessionId, errorFromTikTok]);

  const accounts = session?.pages || [];
  const diagnostic = session?.diagnostic || {};

  async function handleFinalize() {
    if (!pickedId) return;
    setSubmitState({ submitting: true, error: null });
    try {
      const r = await api.post("/publish/oauth/tiktok/finalize", {
        session_id: sessionId,
        channel_id: pickedId,
        display_name: displayName.trim(),
        is_default: isDefault,
      });
      const name = r.data?.connection?.display_name || "TikTok account";
      bounceBack(`Connected ${name}. Videos are sent to your TikTok inbox as drafts; finish and post them in the TikTok app.`);
    } catch (e) {
      setSubmitState({ submitting: false, error: formatApiError(e) });
    }
  }

  if (loadState.loading) {
    return (
      <Shell title="Connect TikTok">
        <LoadingCard label="Loading the TikTok OAuth session…" />
      </Shell>
    );
  }

  return (
    <Shell title="Connect TikTok">
      <div className="bo-page" data-testid="tt-oauth-callback">
        <section className="bo-card">
          <div className="bo-eyebrow">Publish → Channel Connections → TikTok</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Confirm the TikTok account to connect
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 760, lineHeight: 1.55 }}>
            TikTok issued a 24-hour access token and a rotating refresh token.
            Brand OS keeps both encrypted and rotates the refresh token on
            every renewal so scheduled posts keep working.
          </p>

          <Pill data-testid="tt-inbox-pill" style={{ marginTop: 8 }}>
            Videos are sent to your TikTok inbox as drafts. You finish and
            post them in the TikTok app.
          </Pill>

          {loadState.error ? (
            <ErrorBanner data-testid="tt-oauth-error">{loadState.error}</ErrorBanner>
          ) : null}

          {diagnostic.refresh_token_warning ? (
            <ErrorBanner data-testid="tt-refresh-warning" tone="warn">
              {diagnostic.refresh_token_warning}
            </ErrorBanner>
          ) : null}

          {accounts.length === 0 ? (
            <EmptyState
              title="TikTok did not return an account for this token"
              detail={
                tiktokCode || tiktokMessage
                  ? `${tiktokCode ? `TikTok error: ${tiktokCode}. ` : ""}${tiktokMessage || ""}`.trim()
                  : "Make sure you signed in with the TikTok Business account that owns @precipice.health. If this keeps happening, revoke the app under TikTok Settings → Apps and websites, then retry."
              }
              data-testid="tt-no-accounts"
            />
          ) : (
            <>
              <div style={{ display: "grid", gap: 8, margin: "16px 0" }}>
                {accounts.map((acc) => (
                  <label
                    key={acc.id}
                    data-testid={`tt-account-opt-${acc.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      border: "1px solid var(--bo-border)",
                      borderRadius: 6,
                      cursor: "pointer",
                      background:
                        pickedId === String(acc.id)
                          ? "var(--bo-panel-alt)"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="tt-account"
                      value={acc.id}
                      checked={pickedId === String(acc.id)}
                      onChange={(e) => setPickedId(e.target.value)}
                    />
                    {acc.thumbnail_url ? (
                      <img
                        src={acc.thumbnail_url}
                        alt=""
                        width={48}
                        height={48}
                        style={{ borderRadius: "50%" }}
                      />
                    ) : null}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <strong>{acc.title || "TikTok account"}</strong>
                      <span
                        style={{ fontSize: 12, color: "var(--bo-muted)" }}
                        data-testid="tt-account-open-id"
                      >
                        open_id: <code>{acc.id}</code>
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="bo-field" style={{ marginTop: 8 }}>
                <label className="bo-label" htmlFor="tt-display-name">
                  Connection display name
                </label>
                <input
                  id="tt-display-name"
                  className="bo-input"
                  data-testid="tt-display-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="TikTok · Precipice Health"
                />
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  data-testid="tt-is-default-checkbox"
                />
                Make this the default TikTok connection for scheduled posts
              </label>

              {submitState.error ? (
                <ErrorBanner data-testid="tt-finalize-error">
                  {submitState.error}
                </ErrorBanner>
              ) : null}

              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button
                  className="bo-btn bo-btn-primary"
                  data-testid="tt-finalize-btn"
                  onClick={handleFinalize}
                  disabled={!pickedId || submitState.submitting}
                >
                  {submitState.submitting ? "Connecting…" : "Connect this account"}
                </button>
                <button
                  className="bo-btn bo-btn-ghost"
                  data-testid="tt-cancel-btn"
                  onClick={() => bounceBack(null, "User cancelled")}
                  disabled={submitState.submitting}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          <details style={{ marginTop: 24 }}>
            <summary
              style={{ cursor: "pointer", color: "var(--bo-muted)", fontSize: 13 }}
            >
              Diagnostic details
            </summary>
            <pre
              style={{
                fontSize: 12,
                background: "var(--bo-panel-alt)",
                padding: 10,
                borderRadius: 4,
                overflow: "auto",
              }}
            >
              {JSON.stringify(
                {
                  session_id: sessionId,
                  channel: session?.channel,
                  state: session?.state,
                  granted_scopes: diagnostic.granted_scopes,
                  required_scopes: diagnostic.required_scopes,
                  refresh_token_present: diagnostic.refresh_token_present,
                  tiktok_display_name: diagnostic.tiktok_display_name,
                  tiktok_open_id: diagnostic.tiktok_open_id,
                  tiktok_error_code: tiktokCode || undefined,
                  tiktok_error_message: tiktokMessage || undefined,
                },
                null,
                2,
              )}
            </pre>
          </details>
        </section>
      </div>
    </Shell>
  );
}
